<script>
export default {
  name: 'TableRowActions',
  props: {
    actions: {
      type: Array,
      default: () => [],
    },
    defaultColor: {
      type: String,
      default: '#8E8E8E',
    },
    menuIcon: {
      type: String,
      default: 'mdi-dots-vertical',
    },
    menuColor: {
      type: String,
      default: 'grey',
    },
    disabledColor: {
      type: String,
      default: '#C4C4C4',
    },
    iconSize: {
      type: [Number, String],
      default: 16,
    },
    inlineLimit: {
      type: Number,
      default: 4,
    },
  },
  computed: {
    visibleActions() {
      const list = Array.isArray(this.actions) ? this.actions : []
      return list.filter((actionItem) => !!actionItem)
    },
    isInlineMode() {
      const total = this.visibleActions.length
      const hasActions = total > 0
      if (!hasActions) {
        return false
      }
      return total <= this.inlineLimit
    },
    menuNormalActions() {
      return this.visibleActions.filter((action) => !action.danger)
    },
    menuDangerActions() {
      return this.visibleActions.filter((action) => !!action.danger)
    },
    hasMenuDivider() {
      return (
        this.menuDangerActions.length > 0 && this.menuNormalActions.length > 0
      )
    },
  },
  methods: {
    onClick(action) {
      if (!action || action.disabled || action.loading) {
        return
      }
      this.$emit('action', action)
      if (typeof action.handler === 'function') {
        action.handler()
      }
    },
    actionWrapperClass(action) {
      return {
        'table-row-actions__disabled': !!(action && action.disabled),
      }
    },
    getActionColor(action) {
      if (action && action.disabled) {
        return this.disabledColor
      }
      if (action && action.color) {
        return action.color
      }
      return this.defaultColor
    },
    getActionKey(action, index) {
      if (action && action.key) {
        return action.key
      }
      if (action && action.tooltip) {
        return `${action.tooltip}-${index}`
      }
      return index
    },
  },
}
</script>

<template>
  <div class="table-row-actions d-flex align-center justify-content-center">
    <template v-if="isInlineMode">
      <v-tooltip
        v-for="(action, index) in visibleActions"
        :key="getActionKey(action, index)"
        top
      >
        <template v-slot:activator="{ on, attrs }">
          <span
            v-bind="attrs"
            :class="actionWrapperClass(action)"
            v-on="on"
          >
            <v-btn
              icon
              small
              :disabled="!!action.disabled"
              :loading="!!action.loading"
              @click.stop="onClick(action)"
            >
              <icon-svg
                :name="action.icon"
                :color="getActionColor(action)"
                :size="iconSize"
              />
            </v-btn>
          </span>
        </template>
        <span>{{ action.tooltip }}</span>
      </v-tooltip>
    </template>

    <template v-else>
      <v-menu offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon small v-bind="attrs" v-on="on">
            <icon-svg :name="menuIcon" :color="menuColor" :size="iconSize" />
          </v-btn>
        </template>

        <v-list dense>
          <v-list-item
            v-for="(action, index) in menuNormalActions"
            :key="'normal-' + getActionKey(action, index)"
            :disabled="!!action.disabled"
            @click="onClick(action)"
          >
            <v-list-item-icon>
              <icon-svg
                :name="action.icon"
                :color="getActionColor(action)"
                :size="iconSize"
              />
            </v-list-item-icon>
            <v-list-item-title>
              {{ action.tooltip }}
            </v-list-item-title>
          </v-list-item>

          <v-divider v-if="hasMenuDivider" class="table-row-actions__divider" />

          <v-list-item
            v-for="(action, index) in menuDangerActions"
            :key="'danger-' + getActionKey(action, index)"
            class="table-row-actions__menu-item--danger"
            :disabled="!!action.disabled"
            @click="onClick(action)"
          >
            <v-list-item-icon>
              <icon-svg :name="action.icon" color="danger" :size="iconSize" />
            </v-list-item-icon>
            <v-list-item-title class="table-row-actions__danger-text">
              {{ action.tooltip }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
  </div>
</template>

<style lang="sass">
.table-row-actions
  .v-btn--icon
    padding: 0 4px

  &__disabled
    cursor: not-allowed

  &__divider
    margin: 4px 0

.table-row-actions__danger-text
  color: var(--danger-text)
</style>
